import { useCallback, useMemo } from 'react';
import { ExtendedEventSeries } from '../EventCreateSections/EventScheduleSection/extendedEventSeries.model';
import { array, mixed, number, object, string } from 'yup';
import { useLanguage, useHasAccess } from '~/Hooks';
import { FormikErrors } from 'formik';
import { ExtendedEventGroup } from '../ExtendedEventGroup.model';
import { MAX_CANCELATION_WINDOW_INPUT, MAX_CANCELATION_WINDOW_INPUT_USC } from '../../constants';
import { AccessList } from '~/Hooks/useHasAccess/accessList.types';

export const useEventValidationSchema = (
  validate?: (
    values: Partial<ExtendedEventGroup>,
  ) => void | object | Promise<FormikErrors<Partial<ExtendedEventGroup>>>,
) => {
  const { t } = useLanguage();
  const { hasAccess } = useHasAccess();

  const getMessage = useCallback((field: string) => t('form.field.message.required', { field }), [
    t,
  ]);
  const label = useMemo(
    () => ({
      color: t('event.color'),
      spots: t('form.field.spots'),
      title: t('form.field.title'),
      refund: t('app.event.cancelEvent.refund'),
      general: t('app.title.general'),
      details: t('app.title.details'),
      settings: t('app.settings'),
      schedule: t('app.title.schedule'),
      timeSlot: t('event.edit.timeSlot'),
      sportCategory: t('event.sportCategory'),
      trainer: t('form.field.trainer'),
      location: t('form.field.location'),
    }),
    [t],
  );
  const error = useMemo(
    () => ({
      required: {
        name: getMessage(`${label.general} / ${label.title}`),
        color: getMessage(`${label.details} / ${label.color}`),
        seats: getMessage(`${label.general} / ${label.spots}`),
        categoryId: getMessage(`${label.details} / ${label.sportCategory}`),
        timeSeries: getMessage(`${label.schedule} / ${label.timeSlot}`),
        trainerIds: getMessage(`${label.general} / ${label.trainer}`),
        locationId: getMessage(`${label.general} / ${label.location}`),
      },
      min: {
        seats: t('form.field.message.min', {
          field: `${label.general} / ${label.spots}`,
          min: 0,
        }),
        publicCancellationRefundEndAsNumber: t('form.field.message.min', {
          field: `${label.settings} / ${label.refund}`,
          min: 0,
        }),
      },
      max: {
        publicCancellationRefundEndAsNumber: t('form.field.message.max', {
          field: `${label.settings} / ${label.refund}`,
          max: hasAccess(AccessList.CANCELATION_WINDOW_PARTNER_TOOL)
            ? MAX_CANCELATION_WINDOW_INPUT_USC
            : MAX_CANCELATION_WINDOW_INPUT,
        }),
      },
    }),
    [t, label],
  );

  const validationSchema = useMemo(() => {
    const schema = {
      name: string().required(error.required.name),
      seats: number().min(1, error.min.seats).required(error.required.seats),
      color: string().required(error.required.color),
      categoryId: mixed().required(error.required.categoryId),
      timeSeries: array().required(error.required.timeSeries),
      trainerIds: array().required(error.required.trainerIds),
      locationId: string().required(error.required.locationId),
    };
    return object().shape(schema);
  }, [t]);

  const editValidationSchema = useMemo(
    () =>
      object({
        name: string().required(error.required.name),
        seats: number().min(1, error.min.seats).required(error.required.seats),
        trainerIds: array().of(string()).required(error.required.trainerIds),
      }),
    [t],
  );

  const validationWrapper = useCallback(
    (data) => {
      if (!validate) {
        return null;
      }

      const errors = { ...validate(data) };

      if (data.publicCancellationWindowOpen && data.publicCancellationRefundEndAsNumber <= 0) {
        Object.assign(errors, {
          publicCancellationRefundEndAsNumber: error.min.publicCancellationRefundEndAsNumber,
        });
      } else if (
        data.timeSeries.length &&
        !data.timeSeries.some((t: ExtendedEventSeries) => !t.shouldDelete)
      ) {
        Object.assign(errors, {
          timeSeries: error.required.timeSeries,
        });
      }
      if (
        hasAccess(AccessList.CANCELATION_WINDOW_PARTNER_TOOL) &&
        data.publicCancellationRefundEndAsNumber > MAX_CANCELATION_WINDOW_INPUT_USC
      ) {
        Object.assign(errors, {
          publicCancellationRefundEndAsNumber: error.max.publicCancellationRefundEndAsNumber,
        });
      }
      return errors;
    },
    [t, validate],
  );

  return { validationSchema, editValidationSchema, validationWrapper };
};
